import { defineStore } from 'pinia';
import { httpAPI } from '@/services/ajax';
import app from '@/main.js';

export const useEmployeesStore = defineStore('employees', {
    state: () => ({
        employees: [],

        fetching: false, // are we currently fetching the user?
        callbackBus: {}, // functions to call once fetch is done
    }),
    actions: {
        _fetchAndTriggerCallbacks() {
            /** Force fetch the user from the API, then perform all the callbacks in callbackBus */
            if (!app) return;

            this.fetching = true;

            const vm = this;
            const url = app.config.globalProperties.$env.apiRoot + 'user/read';
            const payload = {
                fields: [
                    'first_name',
                    'last_name',
                    'email',
                    'profile_image_id',
                    'hash',
                    'phone',
                    'city',
                    'hire_date',
                    'title',
                    'is_company_admin',
                    'is_wp_connected',
                    'invite_accepted_at',
                    'deactivated_at',
                    'created_at',
                ],
                include: {
                    sites: {
                        fields: ['id', 'name'],
                    },
                    roles: {
                        fields: ['id', 'name'],
                    },
                    key_contacts: true,
                },
            };

            httpAPI(url, payload, 'GET', app.config.globalProperties.$router)
                .then((response) => {
                    if (response?.success && response.data) {
                        Object.freeze(response.data);
                        vm.employees = response.data;
                    }
                    this.fetching = false;
                })
                .catch(() => {
                    this.fetching = false;
                })
                .finally(() => {
                    this.fetching = false;

                    // Perform list of callback functions
                    for (const callbackId in this.callbackBus) {
                        const callback = this.callbackBus[callbackId];
                        try {
                            callback(this.employees);
                        } catch (error) {
                            console.error(
                                'Error in employees store callback',
                                error
                            );
                        }
                        delete this.callbackBus[callbackId]; // Remove it from bus
                    }
                });
        },

        fetchEmployees(callback = null) {
            /** Fetch user from API and perform the given callback once completed */

            // Add callback to our bus
            if (callback) {
                const callbackIds = Object.keys(this.callbackBus);
                const lastId = callbackIds.length
                    ? callbackIds[callbackIds.length - 1]
                    : 0;
                this.callbackBus[lastId + 1] = callback;
            }

            // Only start resyncing if not already resyncing
            if (!this.fetching) this._fetchAndTriggerCallbacks();
        },

        sync(callback = null) {
            /** Fetch the user from the API (if necessary), then perform the callback function once completed */
            if (!this.employees.length) this.fetchEmployees(callback);
            else if (callback) callback(this.employees);
        },

        clear() {
            this.employees = [];
            this.fetching = false;
            this.callbackBus = {};
        },

        getEmployeeById(id) {
            return this.employees.find((emp) => emp.id == id || emp.hash == id);
        },

        getEmployeeOptions(
            ignoreIds = [],
            { ignoreDeactivated = true, ignoreInvited = true } = {}
        ) {
            // First, filter employees based on status criteria
            const filteredEmployees = this.employees.filter((emp) => {
                // Skip deactivated employees if ignoreDeactivated is true
                if (ignoreDeactivated && emp.deactivated_at) {
                    return false;
                }

                // Skip employees who haven't accepted invitation if ignoreInvited is true
                if (ignoreInvited && !emp.invite_accepted_at) {
                    return false;
                }

                // Skip employees if they are in the ignoreIds array
                if (ignoreIds.includes(emp.id)) {
                    return false;
                }

                return true;
            });

            return filteredEmployees.map((emp) => {
                const nameTokens = [];
                if (emp.first_name) nameTokens.push(emp.first_name);
                if (emp.last_name) nameTokens.push(emp.last_name);

                return {
                    text: nameTokens.join(' '),
                    value: String(emp.id),
                };
            });
        },

        /**
         * Get employees filtered by role ID
         * @param {string|number} roleId - The role ID to filter by
         * @returns {Array} Array of employee IDs with the specified role
         */
        getEmployeesByRole(roleId) {
            if (!roleId) return [];

            return this.employees
                .filter((emp) => {
                    return (
                        emp.roles &&
                        Array.isArray(emp.roles) &&
                        emp.roles.some((role) => String(role.id) === String(roleId))
                    );
                })
                .map((emp) => emp.id);
        },

        /**
         * Get employee options (label/value format) filtered by role
         * @param {string|number} roleId - The role ID to filter by
         * @param {string} format - Return format: 'string' or 'number' for value type
         * @returns {Array} Array of {label, value} or {text, value} objects
         */
        getEmployeeOptionsByRole(roleId, format = 'string') {
            if (!roleId) return [];

            const employeesWithRole = this.employees.filter((emp) => {
                return (
                    emp.roles &&
                    Array.isArray(emp.roles) &&
                    emp.roles.some((role) => String(role.id) === String(roleId))
                );
            });

            return employeesWithRole.map((emp) => {
                const nameTokens = [];
                if (emp.first_name) nameTokens.push(emp.first_name);
                if (emp.last_name) nameTokens.push(emp.last_name);
                const name = nameTokens.join(' ');

                const value = format === 'number' ? Number(emp.id) : String(emp.id);

                return {
                    label: name,
                    text: name,
                    value: value,
                };
            });
        },
    },

    getters: {},
});
